import { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../../MetaReportViewer.module.css';
import { Section } from '../../ReportUIPrimitives';
import { PerfField } from './common';
import { fmt } from './commonData';
import { analyzeShopMetaCampaign, getShopMetaCampaignAnalysis } from '../../../../utils/api/reportApi';
import { useAnalysisFlow, AnalysisFlowLog } from './AnalysisFlowLog';

const formatMetric = (value, suffix = '') => (
  value == null ? 'Not available' : `${fmt(value)}${suffix}`
);

// Parses the LLM assessment text (which may include markdown headings like ## SECTION X — TITLE) into structured blocks.
const SECTION_RE = /^(?:#{1,6}\s*)?SECTION\s+[A-Z]+\s*[—–-]+\s*/i;

const stripInlineMarkdown = (value = '') => String(value)
  .replace(/\*\*(.*?)\*\*/g, '$1')
  .replace(/__(.*?)__/g, '$1')
  .replace(/`([^`]+)`/g, '$1')
  .trim();

const normalizeAssessmentText = (value) => {
  if (typeof value === 'string') return value;
  if (!value) return '';
  if (typeof value === 'object') {
    if (typeof value.assessment === 'string') return value.assessment;
    if (typeof value.text === 'string') return value.text;
    if (typeof value.summary === 'string') return value.summary;
  }
  return String(value);
};

const parseAssessmentText = (text = '') => {
  const normalizedText = normalizeAssessmentText(text);
  const lines = normalizedText.split('\n');
  const sections = [];
  let current = null;

  for (const raw of lines) {
    const line = raw.trimEnd();
    const trimmed = line.trim();
    if (SECTION_RE.test(trimmed)) {
      if (current) sections.push(current);
      current = { title: stripInlineMarkdown(trimmed), lines: [] };
    } else if (current) {
      current.lines.push(line);
    } else {
      // preamble before first section
      if (!sections.length) sections.push({ title: '', lines: [] });
      sections[0].lines.push(line);
    }
  }
  if (current) sections.push(current);
  return sections;
};

const KV_RE = /^([A-Za-z][^:]{0,80}):\s*(.+)$/;

const AssessmentLine = ({ line, styles: s }) => {
  const trimmed = stripInlineMarkdown(line.trim());
  if (!trimmed) return null;

  const kv = KV_RE.exec(trimmed);
  if (kv) {
    return (
      <div className={s.matrixAssessmentMetric}>
        <span className={s.matrixAssessmentMetricLabel}>{kv[1].trim()}</span>
        <span className={s.matrixAssessmentMetricValue}>{kv[2].trim()}</span>
      </div>
    );
  }

  // Bullet-like lines (-, •, *, →, numbered)
  const isBullet = /^[-•*→]\s/.test(trimmed) || /^\d+[.)]\s/.test(trimmed);
  if (isBullet) {
    return <li className={s.matrixAssessmentListItem}>{trimmed.replace(/^[-•*→\d.)\s]+/, '').trim()}</li>;
  }

  return <p className={s.matrixAssessmentBody}>{trimmed}</p>;
};

const AssessmentSection = ({ section, styles: s }) => {
  const nonEmpty = section.lines.filter((l) => l.trim());
  if (!section.title && !nonEmpty.length) return null;

  const hasBullets = nonEmpty.some((l) => /^[-•*→]\s/.test(stripInlineMarkdown(l).trim()) || /^\d+[.)]\s/.test(stripInlineMarkdown(l).trim()));

  return (
    <div className={s.matrixAssessmentSection}>
      {section.title && (
        <span className={s.matrixAssessmentSectionTitle}>
          {section.title.replace(SECTION_RE, '').trim() || section.title}
        </span>
      )}
      {hasBullets ? (
        <ul className={s.matrixAssessmentList}>
          {nonEmpty.map((line, i) => (
            <AssessmentLine key={i} line={line} styles={s} />
          ))}
        </ul>
      ) : (
        <div className={s.matrixAssessmentGrid}>
          {nonEmpty.map((line, i) => (
            <AssessmentLine key={i} line={line} styles={s} />
          ))}
        </div>
      )}
    </div>
  );
};

const CampaignAssessmentBody = ({ text, styles: s }) => {
  const normalizedText = useMemo(() => normalizeAssessmentText(text), [text]);
  const sections = useMemo(() => parseAssessmentText(normalizedText), [normalizedText]);
  if (!sections.length) return <p className={s.matrixAssessmentBody}>{normalizedText}</p>;
  // TODO: re-enable performance snapshot once display format is finalized
  const visibleSections = sections.filter(
    (section) => !/performance[\s_-]*snapshot/i.test(section.title)
  );
  return visibleSections.map((section, i) => (
    <AssessmentSection key={i} section={section} styles={s} />
  ));
};

const CAMPAIGN_NUM_FIELDS = [
  'spend', 'impressions', 'clicks', 'link_clicks', 'reach', 'conversions', 'revenue',
  'add_to_cart', 'initiate_checkout', 'landing_page_views', 'engagements',
  'repeat_customers', 'first_time_purchase_count', 'returning_customer_purchase_count',
];

const aggregateCampaignRows = (rows = []) => {
  const grouped = new Map();
  for (const row of rows) {
    const key = String(row?.campaign_id || row?.campaign_name || '').trim();
    if (!key) continue;
    if (!grouped.has(key)) {
      const base = { ...row };
      CAMPAIGN_NUM_FIELDS.forEach((f) => { base[f] = 0; });
      grouped.set(key, base);
    }
    const acc = grouped.get(key);
    CAMPAIGN_NUM_FIELDS.forEach((f) => { acc[f] += Number(row[f]) || 0; });
  }
  return [...grouped.values()].map((row) => {
    const spend = Number(row.spend) || 0;
    const impressions = Number(row.impressions) || 0;
    const clicks = Number(row.clicks) || 0;
    const revenue = Number(row.revenue) || 0;
    const lpv = Number(row.landing_page_views) || 0;
    const conversions = Number(row.conversions) || 0;
    const firstTimePurchase = Number(row.first_time_purchase_count) || 0;
    return {
      ...row,
      roas: spend > 0 ? Number((revenue / spend).toFixed(3)) : 0,
      ctr: impressions > 0 ? Number(((clicks / impressions) * 100).toFixed(2)) : 0,
      cpm: impressions > 0 ? Number(((spend / impressions) * 1000).toFixed(2)) : 0,
      cpc: clicks > 0 ? Number((spend / clicks).toFixed(2)) : null,
      bounce_rate: clicks > 0 ? Number((((clicks - lpv) / clicks) * 100).toFixed(2)) : null,
      aov: conversions > 0 ? Number((revenue / conversions).toFixed(2)) : null,
      cac: firstTimePurchase > 0 ? Number((spend / firstTimePurchase).toFixed(2)) : null,
    };
  }).sort((a, b) => (Number(b.spend) || 0) - (Number(a.spend) || 0));
};

const deriveCampaignRowsForRange = (campaignLevelAssessment, fromMonth, toMonth, selectedWindowKey) => {
  const rangeHelper = campaignLevelAssessment?.range_helper;
  const availableMonths = Array.isArray(rangeHelper?.available_months) ? rangeHelper.available_months : [];

  if (selectedWindowKey && Array.isArray(rangeHelper?.by_window?.[selectedWindowKey]?.rows)) {
    return rangeHelper.by_window[selectedWindowKey].rows;
  }

  if (availableMonths.length && fromMonth && toMonth && rangeHelper?.by_month) {
    const selected = availableMonths.filter((m) => m >= fromMonth && m <= toMonth);
    if (selected.length) {
      const allRows = selected.flatMap((m) => (
        Array.isArray(rangeHelper.by_month[m]?.rows) ? rangeHelper.by_month[m].rows : []
      ));
      const aggregated = aggregateCampaignRows(allRows);
      if (aggregated.length) return aggregated;
    }
  }

  return Array.isArray(campaignLevelAssessment?.all_time?.rows)
    ? campaignLevelAssessment.all_time.rows
    : [];
};

const CampaignLevelAssessmentSection = ({
  campaignLevelAssessment,
  selectedWindowKey,
  currency,
  userId,
  authToken,
  reportId,
  shopId,
  fromMonth,
  toMonth,
  since,
  until,
  onAssessmentSaved,
  onCampaignSelect,
  onShowCampaignPerformance,
}) => {
  const rows = useMemo(
    () => deriveCampaignRowsForRange(campaignLevelAssessment, fromMonth, toMonth, selectedWindowKey),
    [campaignLevelAssessment, fromMonth, toMonth, selectedWindowKey],
  );

  const [selectedKey, setSelectedKey] = useState(null);
  const [fetchedAssessment, setFetchedAssessment] = useState(null);
  const [isAssessmentLoading, setIsAssessmentLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const lastEmittedKeyRef = useRef(null);
  const flow = useAnalysisFlow();

  useEffect(() => {
    if (!rows.length) {
      setSelectedKey(null);
      return;
    }
    const nextKey = rows[0]?.campaign_id || rows[0]?.campaign_name || null;
    setSelectedKey((prev) => {
      if (prev && rows.some((row) => (row.campaign_id || row.campaign_name) === prev)) return prev;
      return nextKey;
    });
  }, [rows]);

  const selectedRow = rows.find((row) => (row.campaign_id || row.campaign_name) === selectedKey) || rows[0] || null;
  const selectedRowKey = selectedRow?.campaign_id || selectedRow?.campaign_name || null;

  useEffect(() => {
    if (typeof onCampaignSelect !== 'function') return;
    if (lastEmittedKeyRef.current === selectedRowKey) return;
    lastEmittedKeyRef.current = selectedRowKey;
    onCampaignSelect(selectedRow || null);
  }, [onCampaignSelect, selectedRow, selectedRowKey]);

  // Reset flow log when a different campaign is selected
  useEffect(() => {
    flow.reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowKey]);

  const assessments = campaignLevelAssessment?.assessments || {};
  const rawPersistedAssessment =
    assessments[selectedRow?.campaign_id] ||
    assessments[selectedRow?.campaign_name] ||
    null;
  // Batch-generated assessments are stored as { assessment, selection_category, selection_score }
  // On-demand assessments are stored as plain strings — normalize to string in both cases
  const persistedAssessment = rawPersistedAssessment
    ? (typeof rawPersistedAssessment === 'object' ? rawPersistedAssessment.assessment || null : rawPersistedAssessment)
    : null;

  const selectedAssessment = fetchedAssessment || persistedAssessment || null;

  useEffect(() => {
    let cancelled = false;
    setAnalysisError('');

    if (!selectedRow) {
      setFetchedAssessment(null);
      setIsAssessmentLoading(false);
      return undefined;
    }

    if (persistedAssessment) {
      setFetchedAssessment(persistedAssessment);
      setIsAssessmentLoading(false);
      return undefined;
    }

    if (!userId || !reportId) {
      setFetchedAssessment(null);
      setIsAssessmentLoading(false);
      return undefined;
    }

    setFetchedAssessment(null);
    setIsAssessmentLoading(true);

    getShopMetaCampaignAnalysis({
      userId,
      reportId,
      shopId,
      campaignId: selectedRow.campaign_id,
      campaignName: selectedRow.campaign_name,
      fromMonth,
      toMonth,
      authToken,
    })
      .then((result) => {
        if (cancelled) return;
        setFetchedAssessment(result?.assessment || null);
      })
      .catch(() => {
        if (cancelled) return;
        setFetchedAssessment(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsAssessmentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    authToken,
    fromMonth,
    persistedAssessment,
    reportId,
    selectedRow,
    selectedRow?.campaign_id,
    selectedRow?.campaign_name,
    shopId,
    toMonth,
    userId,
  ]);

  if (!rows.length) return null;

  const fmtCurrency = (value) => (value != null ? (currency ? `${currency} ${fmt(value)}` : fmt(value)) : 'Not available');
  const detailFields = [
    { label: 'Objective', value: selectedRow.objective || '—' },
    { label: 'Spend', value: fmtCurrency(selectedRow.spend) },
    { label: 'Revenue', value: fmtCurrency(selectedRow.revenue) },
    { label: 'ROAS', value: selectedRow.roas != null ? `${fmt(selectedRow.roas)}×` : 'Not available' },
    { label: 'CPC', value: fmtCurrency(selectedRow.cpc) },
    { label: 'CPM', value: fmtCurrency(selectedRow.cpm) },
    { label: 'Impressions', value: selectedRow.impressions != null ? fmt(selectedRow.impressions, 0) : 'Not available' },
    { label: 'Clicks', value: selectedRow.clicks != null ? fmt(selectedRow.clicks, 0) : 'Not available' },
    { label: 'CTR', value: formatMetric(selectedRow.ctr, '%') },
    { label: 'Bounce Rate', value: formatMetric(selectedRow.bounce_rate, '%') },
    { label: 'Engagements', value: selectedRow.engagements != null ? fmt(selectedRow.engagements, 0) : 'Not available' },
    { label: 'Add to Cart', value: selectedRow.add_to_cart != null ? fmt(selectedRow.add_to_cart, 0) : 'Not available' },
    { label: 'Init. Checkout', value: selectedRow.initiate_checkout != null ? fmt(selectedRow.initiate_checkout, 0) : 'Not available' },
    { label: 'Purchases', value: selectedRow.conversions != null ? fmt(selectedRow.conversions, 0) : 'Not available' },
    { label: 'AOV', value: fmtCurrency(selectedRow.aov) },
    { label: 'New Customers', value: selectedRow.first_time_purchase_count != null ? fmt(selectedRow.first_time_purchase_count, 0) : 'Not available' },
    { label: 'Repeat Customers', value: selectedRow.repeat_customers != null ? fmt(selectedRow.repeat_customers, 0) : 'Not available' },
    { label: 'CAC', value: fmtCurrency(selectedRow.cac) },
  ];

  const handleAnalyze = async () => {
    if (!userId || !selectedRow) return;
    setIsAnalyzing(true);
    setAnalysisError('');
    flow.start();
    try {
      const result = await analyzeShopMetaCampaign({
        userId,
        reportId,
        shopId,
        campaignId: selectedRow.campaign_id,
        campaignName: selectedRow.campaign_name,
        fromMonth,
        toMonth,
        authToken,
      });
      flow.resolve();
      if (result?.assessment_key && result?.assessment && typeof onAssessmentSaved === 'function') {
        setFetchedAssessment(result.assessment);
        onAssessmentSaved(result.assessment_key, result.assessment);
      }
    } catch (error) {
      flow.fail(error?.message || 'Failed to analyze the selected campaign.');
      setAnalysisError(error?.message || 'Failed to analyze the selected campaign.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <Section title="Campaign Analysis">
      <div className={styles.matrixShell}>
        <div className={styles.matrixSelectorCard}>
          <div className={styles.matrixSelectorHeader}>
            <p className={styles.matrixEyebrow}>Campaigns</p>
            <span className={styles.matrixSelectorCount}>{rows.length} total</span>
          </div>
          <div className={styles.matrixSelectorList}>
            {rows.map((row) => {
              const key = row.campaign_id || row.campaign_name || '';
              const isActive = key === (selectedRow?.campaign_id || selectedRow?.campaign_name);
              return (
                <button
                  key={key}
                  type="button"
                  className={`${styles.matrixSelectorItem} ${isActive ? styles.matrixSelectorItemActive : ''}`}
                  onClick={() => setSelectedKey(key)}
                >
                  <span className={styles.matrixSelectorName}>{row.campaign_name || row.campaign_id || 'Campaign'}</span>
                  <span className={styles.matrixSelectorMeta}>
                    {currency ? `${currency} ${fmt(row.spend)}` : fmt(row.spend)} spend
                    {row.roas != null ? ` · ${fmt(row.roas)}× ROAS` : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.matrixDetailCard}>
          <div className={styles.matrixDetailHeader}>
            <div>
              <p className={styles.matrixEyebrow}>Selected Campaign</p>
              <h4 className={styles.matrixDetailTitle}>{selectedRow?.campaign_name || selectedRow?.campaign_id || 'Campaign'}</h4>
            </div>
          </div>
          <div className={styles.matrixActionRow}>
            {/* <button
              type="button"
              className={styles.matrixSecondaryButton}
              onClick={() => {
                if (!selectedRow || typeof onShowCampaignPerformance !== 'function') return;
                onShowCampaignPerformance({
                  campaignId: selectedRow.campaign_id || '',
                  campaignName: selectedRow.campaign_name || '',
                  adsetId: '',
                  adsetName: '',
                });
              }}
            >
              Show raw campaign data
            </button> */}
            {(isAssessmentLoading || !selectedAssessment) ? (
              <button
                type="button"
                className={`${styles.matrixAnalyzeButton} ${(isAnalyzing || isAssessmentLoading) ? styles.matrixAnalyzeButtonBusy : ''}`}
                onClick={handleAnalyze}
                disabled={isAnalyzing || isAssessmentLoading || !userId}
              >
                {isAssessmentLoading ? 'Checking saved analysis…' : isAnalyzing ? 'Analyzing…' : 'Analyze Campaign'}
              </button>
            ) : null}
            {analysisError ? <span className={styles.matrixActionError}>{analysisError}</span> : null}
          </div>
          <div className={styles.perfFields}>
            {detailFields.map((field) => (
              <PerfField key={field.label} label={field.label} value={field.value} />
            ))}
          </div>
        </div>

        <AnalysisFlowLog steps={flow.steps} label="Campaign Analysis Progress" />

        {!isAssessmentLoading && selectedAssessment ? (
          <div className={styles.matrixAssessmentCard}>
            <div className={styles.matrixAssessmentHeader}>
              <p className={styles.matrixEyebrow}>Campaign Analysis</p>
              <span className={styles.matrixAssessmentLabel}>Analyzed</span>
            </div>
            <div className={styles.matrixAssessmentInner}>
              <CampaignAssessmentBody text={selectedAssessment} styles={styles} />
            </div>
          </div>
        ) : null}
      </div>
    </Section>
  );
};

export default CampaignLevelAssessmentSection;
